/**
 * Weapon Damage Calculation Utilities
 *
 * Handles parsing weapon damage format and calculating displayed damage
 * with proficiency multiplier. Proficiency determines the number of
 * damage dice rolled, but does not affect flat modifiers.
 *
 * Examples:
 *   - Weapon: d6, Proficiency: 1 → Display: 1d6
 *   - Weapon: d8+1, Proficiency: 3 → Display: 3d8+1
 *   - Weapon: d10-1, Proficiency: 2 → Display: 2d10-1
 */

import { CharacterData } from '../types/character';

/**
 * Represents parsed weapon damage
 */
export interface ParsedWeaponDamage {
  dieSize: number;      // e.g., 6, 8, 10, 20
  modifier: number;     // e.g., 1, -1, 0
  modifierSign: '+' | '-' | null; // Original sign for formatting
}

/**
 * Gets current proficiency from character (defaults to 1)
 * Safe getter that handles undefined values
 */
export function getProficiency(character: CharacterData): number {
  return character.proficiency || 1;
}

/**
 * Checks if proficiency is at maximum (6)
 */
export function isProficiencyAtMax(proficiency: number): boolean {
  return proficiency >= 6;
}

/**
 * Validates proficiency is in valid range (1-6)
 */
export function isValidProficiency(value: number): boolean {
  return value >= 1 && value <= 6 && Number.isInteger(value);
}

/**
 * Parses weapon damage string into components
 *
 * Expected formats:
 * - "d6" → {dieSize: 6, modifier: 0, modifierSign: null}
 * - "d8+1" → {dieSize: 8, modifier: 1, modifierSign: '+'}
 * - "d10-1" → {dieSize: 10, modifier: -1, modifierSign: '-'}
 *
 * @param damage - Weapon damage string
 * @returns Parsed damage or null if invalid format
 */
export function parseWeaponDamage(damage: string): ParsedWeaponDamage | null {
  if (!damage || typeof damage !== 'string') {
    return null;
  }

  // Match pattern: d{number} optionally followed by +/- and number
  // Examples: d6, d8+1, d10-1
  const regex = /^d(\d+)(([+-])(\d+))?$/;
  const match = damage.trim().match(regex);

  if (!match) {
    return null;
  }

  const dieSize = parseInt(match[1], 10);
  const modifierSign = match[3] as '+' | '-' | null || null;
  const modifierValue = match[4] ? parseInt(match[4], 10) : 0;
  const modifier = modifierSign === '-' ? -modifierValue : modifierValue;

  return {
    dieSize,
    modifier,
    modifierSign
  };
}

/**
 * Calculates displayed weapon damage with proficiency multiplier
 *
 * Multiplies the die count by proficiency, preserves flat modifiers.
 *
 * Examples:
 * - "d6" with proficiency 1 → "1d6"
 * - "d6" with proficiency 3 → "3d6"
 * - "d8+1" with proficiency 2 → "2d8+1"
 * - "d10-1" with proficiency 4 → "4d10-1"
 *
 * @param weaponDamage - Weapon damage string (e.g., "d8+1")
 * @param proficiency - Character proficiency (1-6)
 * @returns Formatted damage string for display
 */
export function calculateWeaponDamage(
  weaponDamage: string,
  proficiency: number
): string {
  const parsed = parseWeaponDamage(weaponDamage);

  // If can't parse, return original string
  if (!parsed) {
    return weaponDamage;
  }

  // Build dice notation: {proficiency}d{dieSize}
  const diceNotation = `${proficiency}d${parsed.dieSize}`;

  // Add modifier if present
  if (parsed.modifier === 0 || !parsed.modifierSign) {
    return diceNotation;
  }

  return `${diceNotation}${parsed.modifierSign}${Math.abs(parsed.modifier)}`;
}

/**
 * Gets formatted damage display for a weapon with proficiency
 *
 * @param weapon - Equipment item with damage property
 * @param proficiency - Character proficiency value
 * @returns Formatted damage string or 'No damage'
 */
export function getWeaponDamageDisplay(
  weapon: any, // EquipmentItem type
  proficiency: number
): string {
  if (!weapon || !weapon.damage) {
    return 'No damage';
  }

  return calculateWeaponDamage(weapon.damage, proficiency);
}

/**
 * Calculates minimum proficiency expected for a given level
 * Based on tier achievement proficiency increases at levels 2, 5, 8
 *
 * Level → Minimum Proficiency:
 * 1 → 1 (start)
 * 2 → 2 (+1 tier achievement)
 * 3-4 → 2
 * 5 → 3 (+1 tier achievement)
 * 6-7 → 3
 * 8 → 4 (+1 tier achievement)
 * 9-10 → 4
 *
 * @param level - Character level (1-10)
 * @returns Expected minimum proficiency
 */
export function getMinimumProficiency(level: number): number {
  let proficiency = 1;

  if (level >= 2) proficiency++; // Level 2
  if (level >= 5) proficiency++; // Level 5
  if (level >= 8) proficiency++; // Level 8

  return proficiency;
}

/**
 * Counts how many proficiency increases from tier achievements up to a level
 *
 * @param level - Character level
 * @returns Number of tier achievement proficiency increases
 */
export function getTierAchievementProficiencyIncreases(level: number): number {
  let increases = 0;

  if (level >= 2) increases++; // Level 2
  if (level >= 5) increases++; // Level 5
  if (level >= 8) increases++; // Level 8

  return increases;
}
